import { Component } from '@theme/component';
import { morph } from '@theme/morph';

/**
 * @typedef {{
 *   id: number | string,
 *   url: string,
 *   handle: string,
 *   title: string,
 *   prefix: string,
 *   styleGroup: string,
 *   color: string,
 *   size?: string | null,
 *   available: boolean,
 *   variantId: number | string | null,
 *   mediaId: number | string | null,
 *   imageUrl?: string | null,
 *   price?: string | null,
 *   compareAtPrice?: string | null,
 *   swatch: string | null
 * }} StyleGroupProduct
 */

/** @type {StyleGroupProduct[] | null} */
let styleGroupIndexCache = null;

/**
 * @returns {StyleGroupProduct[]}
 */
function getStyleGroupIndex() {
  if (styleGroupIndexCache) return styleGroupIndexCache;

  const node = document.getElementById('StyleGroupIndex');
  if (!node?.textContent) {
    styleGroupIndexCache = [];
    return styleGroupIndexCache;
  }

  try {
    styleGroupIndexCache = /** @type {StyleGroupProduct[]} */ (JSON.parse(node.textContent));
  } catch (error) {
    console.error('[style-group-swatches] Failed to parse StyleGroupIndex', error);
    styleGroupIndexCache = [];
  }

  return styleGroupIndexCache;
}

/**
 * Builds stitched color swatches from products that share a style_group prefix
 * (first two hyphen segments, e.g. 10-13062 from 10-13062-0518).
 * Renders every sibling color (no carousel).
 *
 * On product cards: swatch change fetches sibling product card HTML and morphs
 * gallery / title / price / link. On PDP (data-pdp-navigate): navigates.
 *
 * @extends {Component}
 */
class StyleGroupSwatches extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#hydrate();
    if (this.dataset.inlineUpsell === 'true') {
      this.addEventListener('change', this.#onInlineUpsellSwatchChange, true);
    } else if (this.dataset.pdpNavigate === 'true') {
      this.addEventListener('change', this.#onPdpNavigate);
    } else {
      // Capture so VariantPicker (no option-value-id on style-group inputs) never runs.
      this.addEventListener('change', this.#onCardSwatchChange, true);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onPdpNavigate);
    this.removeEventListener('change', this.#onCardSwatchChange, true);
    this.removeEventListener('change', this.#onInlineUpsellSwatchChange, true);
    this.#abortController?.abort();
    super.disconnectedCallback?.();
  }

  get #isPdpNavigate() {
    return this.dataset.pdpNavigate === 'true';
  }

  get #isInlineUpsell() {
    return this.dataset.inlineUpsell === 'true';
  }

  /**
   * Inline upsell card: update image / title / price / ATC for sibling color.
   * @param {Event} event
   */
  #onInlineUpsellSwatchChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    event.stopPropagation();

    const item = this.closest('[data-inline-upsell-item]');
    if (!(item instanceof HTMLElement)) return;

    const url = target.dataset.connectedProductUrl;
    if (!url) return;

    const sibling = getStyleGroupIndex().find(
      (entry) => entry.url === url || String(entry.variantId) === String(target.dataset.variantId)
    );

    const variantId = target.dataset.variantId || (sibling ? String(sibling.variantId || '') : '');
    const available = target.dataset.optionAvailable === 'true';
    const title = sibling?.title || '';
    const color = sibling?.color || target.value || '';
    const size = sibling?.size || '';
    const imageUrl = sibling?.imageUrl || '';
    const price = sibling?.price || '';
    const compare = sibling?.compareAtPrice || '';
    const href = variantId ? `${url}${url.includes('?') ? '&' : '?'}variant=${variantId}` : url;

    const variantInput = item.querySelector('[data-upsell-variant-id]');
    const addButton = item.querySelector('[data-upsell-add]');
    const image = item.querySelector('[data-upsell-image]');
    const imageLink = item.querySelector('[data-upsell-image-link]');
    const titleLink = item.querySelector('[data-upsell-title-link]');
    const titleEl = item.querySelector('.inline-upsells__title');
    const priceEl = item.querySelector('[data-upsell-price]');
    const subtitleEl = item.querySelector('[data-upsell-subtitle]');
    const variantLabel = item.querySelector('[data-upsell-variant-label]');

    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
      variantInput.disabled = !available;
    }
    if (addButton instanceof HTMLButtonElement) addButton.disabled = !available;
    if (imageLink instanceof HTMLAnchorElement) imageLink.href = href;
    if (titleLink instanceof HTMLAnchorElement) titleLink.href = href;
    if (image instanceof HTMLImageElement && imageUrl) {
      // srcset from image_tag wins over src — clear so color swap shows.
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = imageUrl;
      if (title) image.alt = title;
    }
    if (titleEl && title) titleEl.textContent = title;
    if (title) item.setAttribute('aria-label', title);

    if (priceEl && price) {
      if (compare) {
        priceEl.innerHTML =
          '<span class="inline-upsells__price-sale">' +
          price +
          '</span><s class="inline-upsells__price-compare">' +
          compare +
          '</s>';
      } else {
        priceEl.innerHTML = '<span>' + price + '</span>';
      }
    }

    if (subtitleEl) {
      if (size && color) {
        subtitleEl.innerHTML =
          size + ' | <span class="inline-upsells__variant" data-upsell-variant-label>' + color + '</span>';
      } else if (variantLabel && color) {
        variantLabel.textContent = color;
      } else if (color) {
        subtitleEl.textContent = color;
      } else if (size) {
        subtitleEl.textContent = size;
      }
    }

    this.dataset.productId = sibling ? String(sibling.id) : this.dataset.productId;
    this.dataset.productUrl = url;
    if (variantId) this.dataset.variantId = variantId;
    this.dataset.color = color;
    this.dataset.available = String(available);

    if (sibling?.id) {
      for (const radio of this.querySelectorAll('input[type="radio"]')) {
        if (radio instanceof HTMLInputElement) {
          radio.name = `style-group-upsell-${sibling.id}`;
        }
      }
    }
  };

  /**
   * @param {Event} event
   */
  #onPdpNavigate = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const url = target.dataset.connectedProductUrl;
    const currentPath = this.dataset.productUrl?.split('?')[0];
    if (!url || url === currentPath) return;

    const next = new URL(url, window.location.origin);
    if (target.dataset.variantId) {
      next.searchParams.set('variant', target.dataset.variantId);
    }
    window.location.assign(next.pathname + next.search);
  };

  /**
   * Product-card: swap card content to the connected sibling product.
   * @param {Event} event
   */
  #onCardSwatchChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    // Block swatches-variant-picker-component / VariantPicker — style-group inputs
    // have no data-option-value-id and would throw in selectedOptionsValues.
    event.stopPropagation();

    const productCard = this.closest('product-card');
    if (!(productCard instanceof HTMLElement)) return;

    const url = target.dataset.connectedProductUrl;
    if (!url) return;

    const currentPath = this.dataset.productUrl?.split('?')[0];
    if (url === currentPath) {
      const mediaId = target.dataset.optionMediaId;
      if (mediaId && 'previewVariant' in productCard && typeof productCard.previewVariant === 'function') {
        productCard.previewVariant(mediaId);
      }
      return;
    }

    this.#swapCardProduct(productCard, target);
  };

  /**
   * @param {HTMLElement} productCard
   * @param {HTMLInputElement} input
   */
  async #swapCardProduct(productCard, input) {
    const productUrl = input.dataset.connectedProductUrl;
    if (!productUrl) return;

    const requestUrl = new URL(productUrl, window.location.origin);
    requestUrl.searchParams.set('section_id', 'section-rendering-product-card');
    if (input.dataset.variantId) {
      requestUrl.searchParams.set('variant', input.dataset.variantId);
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    productCard.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(requestUrl.pathname + requestUrl.search, {
        signal: this.#abortController.signal,
      });
      if (!response.ok) throw new Error(`Card fetch failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.#applyCardUpdate(productCard, html, input);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('[style-group-swatches] Failed to update product card', error);
    } finally {
      productCard.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {HTMLElement} productCard
   * @param {Document} html
   * @param {HTMLInputElement} input
   */
  #applyCardUpdate(productCard, html, input) {
    const source = html.querySelector('[data-style-group-card-source]');
    if (!(source instanceof HTMLElement)) {
      console.error('[style-group-swatches] Missing card morph source in section response');
      return;
    }

    const newProductId = source.dataset.productId || '';
    const newProductUrl = source.dataset.productUrl || input.dataset.connectedProductUrl || '';
    const newTitle = source.dataset.productTitle || '';

    // Gallery / slideshow
    const liveSlideshow = productCard.querySelector('.card-gallery slideshow-component');
    const newSlideshow = source.querySelector('[data-morph-gallery] slideshow-component');
    if (liveSlideshow && newSlideshow) {
      morph(liveSlideshow, newSlideshow);
    }

    // Title (product-title block + visually-hidden link text)
    if (newTitle) {
      for (const el of productCard.querySelectorAll(
        'product-title .title-text, product-title p, .product-card__link .visually-hidden, .product-grid-view-zoom-out--details h3'
      )) {
        el.textContent = newTitle;
      }
    }

    // Capacity | color line
    const liveVariantInfo = productCard.querySelector('.custom-product-card-variant-info');
    const newVariantInfo = source.querySelector('[data-morph-variant-info] .custom-product-card-variant-info');
    if (liveVariantInfo && newVariantInfo) {
      morph(liveVariantInfo, newVariantInfo);
    } else if (liveVariantInfo && !newVariantInfo) {
      liveVariantInfo.remove();
    } else if (!liveVariantInfo && newVariantInfo) {
      const swatches = productCard.querySelector('product-swatches');
      swatches?.insertAdjacentElement('beforebegin', newVariantInfo.cloneNode(true));
    }

    // Price (may appear more than once — main + zoom-out)
    const newPrice = source.querySelector('[data-morph-price] product-price');
    if (newPrice) {
      for (const livePrice of productCard.querySelectorAll('product-price')) {
        morph(livePrice, newPrice.cloneNode(true));
      }
    }

    // Product URL
    const newAnchor = source.querySelector('[data-morph-url]');
    const href =
      newAnchor instanceof HTMLAnchorElement && newAnchor.getAttribute('href')?.trim()
        ? newAnchor.href
        : new URL(newProductUrl, window.location.origin).href;

    const mainLink = productCard.querySelector('a[ref="productCardLink"]');
    if (mainLink instanceof HTMLAnchorElement) {
      mainLink.href = href;
    }

    // product-card + product-card-link ids
    if (newProductId) {
      productCard.dataset.productId = newProductId;
      const cardLink = productCard.closest('product-card-link');
      if (cardLink instanceof HTMLElement) {
        cardLink.dataset.productId = newProductId;
        if (source.dataset.featuredMediaUrl) {
          cardLink.dataset.featuredMediaUrl = source.dataset.featuredMediaUrl;
        }
      }
      for (const gallery of productCard.querySelectorAll('.card-gallery[data-product-id]')) {
        gallery.setAttribute('data-product-id', newProductId);
      }
    }

    // style-group host attrs + radio name for new product
    this.dataset.productId = newProductId;
    this.dataset.productUrl = newProductUrl;
    if (input.dataset.variantId) this.dataset.variantId = input.dataset.variantId;
    if (input.dataset.optionMediaId) this.dataset.mediaId = input.dataset.optionMediaId;
    this.dataset.color = input.value;
    this.dataset.available = input.dataset.optionAvailable || 'true';

    const picker = this.querySelector('swatches-variant-picker-component');
    if (picker instanceof HTMLElement) {
      picker.dataset.productId = newProductId;
      picker.dataset.productUrl = newProductUrl;
    }

    if (newProductId) {
      for (const radio of this.querySelectorAll('input[type="radio"]')) {
        if (radio instanceof HTMLInputElement) {
          radio.name = `style-group-${newProductId}`;
        }
      }
    }

    // Refresh quick-add cache for the new product URL
    const quickAdd = productCard.querySelector('quick-add-component');
    if (quickAdd && typeof quickAdd.fetchProductPage === 'function') {
      quickAdd.fetchProductPage(href);
    }
  }

  #hydrate() {
    const prefix = this.dataset.stylePrefix;
    const productId = this.dataset.productId;
    if (!prefix || !productId) return;

    const siblings = getStyleGroupIndex()
      .filter((item) => item.prefix === prefix)
      .sort((a, b) => String(a.color || a.title).localeCompare(String(b.color || b.title)));

    if (siblings.length <= 1) return;

    const list = this.querySelector('[data-style-group-list]');
    const seed = this.querySelector('[data-style-group-seed]');
    if (!list || !seed) return;

    seed.remove();

    const fragment = document.createDocumentFragment();
    const pdpNavigate = this.#isPdpNavigate;
    const inlineUpsell = this.#isInlineUpsell;
    const radioName = inlineUpsell ? `style-group-upsell-${productId}` : `style-group-${productId}`;

    for (const sibling of siblings) {
      const li = document.createElement('li');
      li.className = 'variant-option__swatch style-group-swatches__item';
      if (!sibling.available) li.classList.add('is-unavailable');

      const colorName = sibling.color || sibling.title;
      const label = document.createElement('label');
      label.className = 'variant-option__button-label variant-option__button-label--has-swatch';
      if (pdpNavigate) label.classList.add('style-group-swatches__label--pdp');
      if (!pdpNavigate && !inlineUpsell && sibling.mediaId) {
        label.dataset.mediaId = String(sibling.mediaId);
      }

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = radioName;
      input.value = colorName;
      input.setAttribute('aria-label', colorName);
      input.dataset.optionAvailable = String(sibling.available);
      input.dataset.connectedProductUrl = sibling.url;
      if (sibling.variantId) input.dataset.variantId = String(sibling.variantId);
      if (sibling.mediaId) input.dataset.optionMediaId = String(sibling.mediaId);
      if (sibling.imageUrl) input.dataset.swatchImage = sibling.imageUrl;
      if (String(sibling.id) === String(productId)) input.checked = true;

      // PDP: product first-image thumb. Cards / upsell: color CSS only.
      const colorBg = sibling.swatch || '';

      if (pdpNavigate) {
        const thumb = document.createElement('span');
        thumb.className = 'style-group-swatches__thumb';
        thumb.setAttribute('role', 'group');

        if (sibling.imageUrl) {
          const img = document.createElement('img');
          img.className = 'style-group-swatches__img';
          img.src = sibling.imageUrl;
          img.alt = colorName;
          img.width = 88;
          img.height = 88;
          img.loading = 'lazy';
          thumb.append(img);
        } else {
          const swatch = document.createElement('span');
          swatch.className = colorBg ? 'swatch' : 'swatch swatch--unavailable';
          if (colorBg) swatch.style.setProperty('--swatch-background', colorBg);
          thumb.append(swatch);
        }

        const tip = document.createElement('span');
        tip.className = 'style-group-swatches__tooltip';
        tip.setAttribute('role', 'tooltip');
        tip.textContent = colorName;
        thumb.append(tip);

        label.append(input, thumb);
      } else {
        const swatch = document.createElement('span');
        swatch.className = colorBg ? 'swatch' : 'swatch swatch--unavailable';
        swatch.title = colorName;
        if (colorBg) swatch.style.setProperty('--swatch-background', colorBg);
        label.append(input, swatch);
      }

      li.append(label);
      fragment.append(li);
    }

    list.append(fragment);
  }
}

if (!customElements.get('style-group-swatches')) {
  customElements.define('style-group-swatches', StyleGroupSwatches);
}
